import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { useCampaignSourceOptions } from '#/features/campaign-enrol'
import { shipmentStatusLabel } from '#/entities/shipment'
import { useCampaignFilterFieldsApiV1CampaignsFilterFieldsGet } from '#/shared/api/generated/communication/theCargoCommunication'
import type { CampaignFilterField } from '#/shared/api/generated/communication/models'
import type { MultiSelectOption } from '#/shared/ui/multi-select'

/** Field types the service publishes for the rolling filter. */
export const CAMPAIGN_FILTER_FIELD_TYPES = {
  enum: 'enum',
  multiEnum: 'multi_enum',
  days: 'days',
} as const

export type CampaignFilterValue = string | string[] | number

/** One chip on the "everyone who matches" option. */
export interface CampaignJoinFilterChip {
  key: string
  label: string
}

interface SetFilter {
  value: CampaignFilterValue
  /** How the value reads — an id list from a picker shows names, not ids.
   *  Absent for a seeded value, which reads as the raw value until re-picked. */
  shown?: string
}

function shownValue(value: CampaignFilterValue): string {
  return Array.isArray(value) ? value.join(', ') : String(value)
}

/**
 * A chosen value, in the product's own words. The service publishes the raw
 * enum a `filter_definition` takes back (`follow_up`, `on_hold`), and a chip
 * is not the one place in the app allowed to say `follow_up` out loud — the
 * stage and status fields both name shipment statuses, so they read the way a
 * row, a tab and the detail toolbar read. A value the status vocabulary has
 * not learned still title-cases rather than showing its underscores.
 */
export function filterValueLabel(value: string, t: TFunction): string {
  return shipmentStatusLabel(value, t)
}

function shownEnum(value: CampaignFilterValue, t: TFunction): string {
  const values = Array.isArray(value) ? value : [value]
  return values.map((item) => filterValueLabel(String(item), t)).join(', ')
}

/** A choice the service publishes with its own values — a status or a stage,
 *  as against a source field, whose values are ids its picker names. */
function isValueField(field: CampaignFilterField): boolean {
  return (
    (field.type === CAMPAIGN_FILTER_FIELD_TYPES.enum ||
      field.type === CAMPAIGN_FILTER_FIELD_TYPES.multiEnum) &&
    !isSourceField(field)
  )
}

/** A multi-choice field the service publishes without options holds source
 *  ids — the org's curated list, not a fixed enum. */
function isSourceField(field: CampaignFilterField): boolean {
  return (
    field.type === CAMPAIGN_FILTER_FIELD_TYPES.multiEnum &&
    (field.options?.length ?? 0) === 0
  )
}

/** An id list, as the names its picker shows. An id the list does not carry
 *  reads as itself rather than vanishing from the chip. */
export function optionNames(
  ids: string[],
  options: MultiSelectOption[],
): string {
  return ids
    .map((id) => options.find((option) => option.value === id)?.label ?? id)
    .join(', ')
}

/** A seeded id list reads as names once the source list is in. */
function shownSources(
  value: CampaignFilterValue,
  sources: MultiSelectOption[],
): string {
  if (!Array.isArray(value)) return shownValue(value)
  return optionNames(value, sources)
}

function chipLabel(
  field: CampaignFilterField,
  set: SetFilter,
  sources: MultiSelectOption[],
  t: TFunction,
) {
  const unit = field.unit != null ? ` ${field.unit}` : ''
  const shown = set.shown ?? shownFor(field, set.value, sources, t)
  return `${field.label}: ${shown}${unit}`
}

function shownFor(
  field: CampaignFilterField,
  value: CampaignFilterValue,
  sources: MultiSelectOption[],
  t: TFunction,
): string {
  if (isSourceField(field)) return shownSources(value, sources)
  if (isValueField(field)) return shownEnum(value, t)
  return shownValue(value)
}

function isFilterValue(value: unknown): value is CampaignFilterValue {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    (Array.isArray(value) && value.every((item) => typeof item === 'string'))
  )
}

/** A saved `filter_definition`, as the builder's own state. */
function seedValues(
  definition: Record<string, unknown>,
): Partial<Record<string, SetFilter>> {
  return Object.fromEntries(
    Object.entries(definition).flatMap(([key, value]) =>
      isFilterValue(value) ? [[key, { value }]] : [],
    ),
  )
}

/**
 * The rolling filter, built from `GET /campaigns/filter-fields`: the service
 * says which keys exist and what each accepts, and the object here is exactly
 * what `filter_definition` takes back — so the builder never invents a key
 * the enroller would ignore. Fetched only while the filter option is chosen.
 * A seed opens the builder on a campaign's saved definition.
 */
export function useCampaignJoinFilters(
  enabled: boolean,
  seed?: Record<string, unknown>,
) {
  const { t } = useTranslation()
  const query = useCampaignFilterFieldsApiV1CampaignsFilterFieldsGet(
    undefined,
    {
      query: { enabled },
    },
  )
  const fields = useMemo(() => query.data?.data ?? [], [query.data])
  const [values, setValues] = useState<Partial<Record<string, SetFilter>>>(
    () => (seed == null ? {} : seedValues(seed)),
  )
  // Only a seeded source chip needs the names; a picked one carries its own.
  const needsSourceNames = fields.some(
    (field) => isSourceField(field) && values[field.key]?.shown == null,
  )
  const sources = useCampaignSourceOptions(enabled && needsSourceNames)

  const setFilter = useCallback(
    (key: string, value: CampaignFilterValue, shown?: string) => {
      setValues((current) => ({ ...current, [key]: { value, shown } }))
    },
    [],
  )
  const removeFilter = useCallback((key: string) => {
    setValues((current) => {
      const next = { ...current }
      delete next[key]
      return next
    })
  }, [])

  const chips: CampaignJoinFilterChip[] = useMemo(
    () =>
      fields.flatMap((field) => {
        const set = values[field.key]
        return set == null
          ? []
          : [
              {
                key: field.key,
                label: chipLabel(field, set, sources.options, t),
              },
            ]
      }),
    [fields, values, sources.options, t],
  )
  const available = useMemo(
    () => fields.filter((field) => values[field.key] == null),
    [fields, values],
  )
  const definition = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(values).flatMap(([key, set]) =>
          set == null ? [] : [[key, set.value]],
        ),
      ) as Record<string, unknown>,
    [values],
  )

  return {
    fields,
    isLoading: query.isLoading,
    chips,
    available,
    setFilter,
    removeFilter,
    definition,
  }
}

export type UseCampaignJoinFiltersResult = ReturnType<
  typeof useCampaignJoinFilters
>
